export const extractFilenameFromUrl = (url: string): string => {
  // Extract the NPZ filename from the full URL, including slice prefix
  const match = url.match(/\/(?:SLICE_\d+_)?NPZ_([^?]+)\.npz/);
  if (match) {
    return `NPZ_${match[1]}.npz`;
  }
  return url;
};

export const getBaseFilename = (filename: string): string => {
  // Remove slice prefix if present and extract the study ID
  const cleanName = filename.replace(/^SLICE_\d+_/, "");
  const match = cleanName.match(/NPZ_(.+?)\.npz/);
  if (!match) return filename || "";
  return match[1];
};

/**
 * Extract DICOM filename from a Cornerstone imageId.
 *
 * ImageId formats:
 * - Direct: wadouri:https://bucket.../path/1.2.826...001.dcm?X-Amz-...
 * - Proxy:  wadouri:http://localhost:8890/api/proxy_dicom?url=https%3A%2F%2F...%2F1.2.826...001.dcm%3F...
 *
 * @param imageId Cornerstone image ID string
 * @returns DICOM filename (e.g., "1.2.826.0.1.123456.001.dcm")
 */
export const extractFilenameFromImageId = (imageId: string): string => {
  // Remove wadouri: prefix
  const url = imageId.replace(/^wadouri:/, "");

  // Check if it's a proxy URL
  if (url.includes("proxy_dicom?url=")) {
    // Extract the encoded S3 URL from the proxy parameter
    const encodedUrl = url.split("proxy_dicom?url=")[1] || "";
    const decodedUrl = decodeURIComponent(encodedUrl);
    // Remove query params and get filename
    const pathPart = decodedUrl.split("?")[0];
    return pathPart.split("/").pop() || "";
  }

  // Direct URL: just extract filename before query params
  const pathPart = url.split("?")[0];
  return pathPart.split("/").pop() || "";
};

/**
 * Build a filename-to-index mapping from an array of Cornerstone imageIds.
 *
 * This is used by the AWS backend pathway where top_slice is stored as a
 * DICOM filename rather than a numeric index.
 *
 * NOTE: For S3 loading via backend metadata, use buildFilenameToIndexMapFromMetadata instead,
 * since metadata includes instance_uid directly.
 *
 * @param imageIds Array of Cornerstone image IDs (sorted by instance number)
 * @returns Map of filename to array index
 */
export const buildFilenameToIndexMap = (imageIds: string[]): Record<string, number> => {
  const mapping: Record<string, number> = {};

  imageIds.forEach((imageId, index) => {
    if (!imageId) return; // Skip empty placeholder entries

    const filename = extractFilenameFromImageId(imageId);
    if (filename) {
      mapping[filename] = index;
    }
  });

  return mapping;
};

/**
 * Instance info for building filename mapping
 */
interface InstanceForMapping {
  instance_uid: string;
  instance_number: number;
}

/**
 * Build a filename-to-index mapping from backend metadata.
 *
 * The backend stores slice_idx as DICOM instance UID (e.g., "1.2.826...001.dcm").
 * This function builds the mapping directly from backend metadata which has instance_uid.
 *
 * @param instances Instances with instance_uid and instance_number (sorted by z_position)
 * @returns Map of DICOM filename to array index
 */
export const buildFilenameToIndexMapFromMetadata = (
  instances: InstanceForMapping[],
): Record<string, number> => {
  const mapping: Record<string, number> = {};

  // Instances arrive pre-sorted by z_position from backend (anatomical order)
  // Do NOT re-sort - must match order used in dicomMetadataLoader.ts imageId building
  instances.forEach((instance, index) => {
    if (!instance.instance_uid) return;

    // Backend stores slice_idx as "instance_uid.dcm"
    const filename = `${instance.instance_uid}.dcm`;
    mapping[filename] = index;
  });

  return mapping;
};
